import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, TrendingUp, BarChart3, ChevronDown, X } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// =============================================================================
// Module-scope constants — these objects are referentially stable across
// renders, so Recharts subtrees don't re-mount unnecessarily. Moving them
// out of the component body is strictly more efficient than useMemo
// (zero runtime cost) since none of them depend on props or state.
// =============================================================================

const COLORS = ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899"];

const LINE_CHART_MARGIN = { top: 5, right: 30, left: 20, bottom: 60 };
const BAR_CHART_MARGIN = { top: 5, right: 30, left: 20, bottom: 80 };
const PIE_CHART_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };

const AXIS_TICK_SM = { fill: "#9ca3af", fontSize: 10 };
const AXIS_TICK_MD = { fill: "#9ca3af", fontSize: 11 };
const AXIS_TICK_LG = { fill: "#9ca3af", fontSize: 12 };

const LEGEND_STYLE = { color: "#9ca3af", paddingTop: "10px" };

const LINE_DOT_RED = { fill: "#ef4444", r: 4 };
const LINE_DOT_GREEN = { fill: "#10b981", r: 3 };
const LINE_DOT_AMBER = { fill: "#f59e0b", r: 3 };

const PIE_LABEL_STYLE = { fontSize: "11px", fontWeight: "bold" };
const PIE_TOOLTIP_WRAPPER = { zIndex: 1000 };
const PIE_TOOLTIP_CURSOR = { fill: "rgba(255, 255, 255, 0.1)" };

// Stateless tooltip — hoisted out of the component so it isn't re-declared
// on every render. Recharts passes `active`, `payload`, and `label` props.
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#1a1a1b] border border-red-900/30 p-3 rounded-lg shadow-lg">
      <p className="text-white font-semibold mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey || entry.name} className="text-white text-sm font-medium">
          <span style={{ color: entry.color, fontWeight: "bold" }}>●</span> {entry.name}: {entry.value}
        </p>
      ))}
    </div>
  );
};

// Stable JSX element for `<Tooltip content={...} />` — Recharts checks
// referential equality on `content`, so reusing one element avoids
// re-mounting the tooltip subtree on each parent render.
const CUSTOM_TOOLTIP_EL = <CustomTooltip />;

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trendsData, setTrendsData] = useState([]);
  const [websiteData, setWebsiteData] = useState([]);
  const [trendsPeriod, setTrendsPeriod] = useState("daily");
  const [selectedWebsites, setSelectedWebsites] = useState([]);
  const [websiteList, setWebsiteList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    fetchWebsiteList();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [trendsPeriod, selectedWebsites]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest(".relative")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const fetchWebsiteList = async () => {
    try {
      const response = await axiosInstance.get("/website-list");
      setWebsiteList(response.data);
    } catch (error) {
      console.error("Error fetching website list:", error);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const websiteParam =
        selectedWebsites.length > 0 ? `&websites=${selectedWebsites.join(",")}` : "";

      const [trendsResponse, websiteResponse] = await Promise.all([
        axiosInstance.get(`/analytics/member-trends?period=${trendsPeriod}${websiteParam}`),
        axiosInstance.get(`/analytics/website-performance?${websiteParam.slice(1)}`),
      ]);

      setTrendsData(trendsResponse.data);
      setWebsiteData(websiteResponse.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Gagal memuat data analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleWebsiteToggle = (websiteName) => {
    setSelectedWebsites((prev) =>
      prev.includes(websiteName) ? prev.filter((w) => w !== websiteName) : [...prev, websiteName]
    );
  };

  const handleSelectAll = () => {
    if (selectedWebsites.length === websiteList.length) {
      setSelectedWebsites([]);
    } else {
      setSelectedWebsites(websiteList.map((w) => w.name));
    }
  };

  const getFilterLabel = () => {
    if (selectedWebsites.length === 0) return "Semua Website";
    if (selectedWebsites.length === 1) return selectedWebsites[0];
    return `${selectedWebsites.length} Website Dipilih`;
  };

  // Stable label renderer for the pie chart. Depends on no state/props,
  // so the callback identity is preserved across renders.
  const renderPieLabel = useCallback(({ website, percent }) => {
    const shortName = website.length > 10 ? website.substring(0, 10) + "..." : website;
    return `${shortName}: ${(percent * 100).toFixed(0)}%`;
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/dashboard")}
                variant="ghost"
                size="sm"
                className="text-gray-300 hover:text-white hover:bg-red-900/20"
              >
                <ArrowLeft className="mr-2" size={18} />
                Kembali
              </Button>
              <h1 className="text-2xl font-bold text-red-50">Analytics Dashboard</h1>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-400 text-sm">Filter Website:</span>
              <div className="relative">
                <Button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  variant="outline"
                  className="w-[240px] bg-[#0f0f10] border-red-900/30 text-white hover:bg-[#1a1a1b] justify-between"
                >
                  <span className="truncate">{getFilterLabel()}</span>
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
                {dropdownOpen && (
                  <div className="absolute top-full mt-1 w-[300px] bg-[#1a1a1b] border border-red-900/30 rounded-lg shadow-lg z-50 max-h-[400px] overflow-y-auto">
                    <div className="p-3 border-b border-red-900/30 sticky top-0 bg-[#1a1a1b]">
                      <div className="flex items-center justify-between">
                        <Button
                          onClick={handleSelectAll}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20 text-xs"
                        >
                          {selectedWebsites.length === websiteList.length ? "Batal Semua" : "Pilih Semua"}
                        </Button>
                        {selectedWebsites.length > 0 && (
                          <Button
                            onClick={() => setSelectedWebsites([])}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-white hover:bg-red-900/20"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="p-2">
                      {websiteList.map((website) => (
                        <div
                          key={website.id}
                          className="flex items-center space-x-2 p-2 hover:bg-red-900/10 rounded cursor-pointer"
                          onClick={() => handleWebsiteToggle(website.name)}
                        >
                          <Checkbox
                            checked={selectedWebsites.includes(website.name)}
                            onCheckedChange={() => handleWebsiteToggle(website.name)}
                            className="border-red-900/30 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                          />
                          <span className="text-white text-sm flex-1">{website.name}</span>
                        </div>
                      ))}
                    </div>
                    <div className="p-2 border-t border-red-900/30 sticky bottom-0 bg-[#1a1a1b]">
                      <Button
                        onClick={() => setDropdownOpen(false)}
                        className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                        size="sm"
                      >
                        Terapkan Filter
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Member Trends Chart */}
            <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-red-50 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-red-500" />
                      Tren Member Baru
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Analisis pertumbuhan member dari waktu ke waktu
                    </CardDescription>
                  </div>
                  <Select value={trendsPeriod} onValueChange={setTrendsPeriod}>
                    <SelectTrigger className="w-[180px] bg-[#0f0f10] border-red-900/30 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1b] border-red-900/30 text-white">
                      <SelectItem value="daily" className="text-white hover:bg-red-900/20">
                        Harian (30 hari)
                      </SelectItem>
                      <SelectItem value="weekly" className="text-white hover:bg-red-900/20">
                        Mingguan (12 minggu)
                      </SelectItem>
                      <SelectItem value="monthly" className="text-white hover:bg-red-900/20">
                        Bulanan (12 bulan)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {trendsData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">Tidak ada data untuk periode ini</div>
                ) : (
                  <ResponsiveContainer width="100%" height={450}>
                    <LineChart data={trendsData} margin={LINE_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date"
                        stroke="#9ca3af"
                        tick={AXIS_TICK_MD}
                        angle={-35}
                        textAnchor="end"
                        height={80}
                        interval={0}
                      />
                      <YAxis stroke="#9ca3af" tick={AXIS_TICK_LG} />
                      <Tooltip content={CUSTOM_TOOLTIP_EL} />
                      <Legend wrapperStyle={LEGEND_STYLE} />
                      <Line
                        type="monotone"
                        dataKey="total_members"
                        stroke="#ef4444"
                        strokeWidth={3}
                        name="Total Member"
                        dot={LINE_DOT_RED}
                      />
                      <Line
                        type="monotone"
                        dataKey="ndp_count"
                        stroke="#10b981"
                        strokeWidth={2}
                        name="NDP"
                        dot={LINE_DOT_GREEN}
                      />
                      <Line
                        type="monotone"
                        dataKey="regis_count"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        name="Regis"
                        dot={LINE_DOT_AMBER}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Website Performance Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Bar Chart */}
              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-red-50 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-red-500" />
                    Performa per Website
                  </CardTitle>
                  <CardDescription className="text-gray-400">Total member berdasarkan website</CardDescription>
                </CardHeader>
                <CardContent>
                  {websiteData.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">Tidak ada data website</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={websiteData} margin={BAR_CHART_MARGIN}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="website"
                          stroke="#9ca3af"
                          tick={AXIS_TICK_SM}
                          angle={-40}
                          textAnchor="end"
                          height={100}
                          interval={0}
                        />
                        <YAxis stroke="#9ca3af" tick={AXIS_TICK_LG} />
                        <Tooltip content={CUSTOM_TOOLTIP_EL} />
                        <Legend wrapperStyle={LEGEND_STYLE} />
                        <Bar dataKey="ndp_count" fill="#10b981" name="NDP" />
                        <Bar dataKey="regis_count" fill="#f59e0b" name="Regis" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
                <CardHeader>
                  <CardTitle className="text-red-50">Distribusi Member per Website</CardTitle>
                  <CardDescription className="text-gray-400">Proporsi member di setiap website</CardDescription>
                </CardHeader>
                <CardContent>
                  {websiteData.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">Tidak ada data website</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart margin={PIE_CHART_MARGIN}>
                        <Pie
                          data={websiteData}
                          dataKey="total_members"
                          nameKey="website"
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          label={renderPieLabel}
                          labelStyle={PIE_LABEL_STYLE}
                        >
                          {websiteData.map((entry, index) => (
                            <Cell key={entry.website} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          content={CUSTOM_TOOLTIP_EL}
                          wrapperStyle={PIE_TOOLTIP_WRAPPER}
                          cursor={PIE_TOOLTIP_CURSOR}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Website Details Table */}
            <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
              <CardHeader>
                <CardTitle className="text-red-50">Detail Performa Website</CardTitle>
                <CardDescription className="text-gray-400">Ringkasan statistik lengkap per website</CardDescription>
              </CardHeader>
              <CardContent>
                {websiteData.length === 0 ? (
                  <div className="text-center py-8 text-gray-400">Tidak ada data website</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-red-900/30">
                          <th className="text-left py-3 px-4 text-red-400 font-semibold">Website</th>
                          <th className="text-center py-3 px-4 text-red-400 font-semibold">Total Member</th>
                          <th className="text-center py-3 px-4 text-red-400 font-semibold">NDP</th>
                          <th className="text-center py-3 px-4 text-red-400 font-semibold">Regis</th>
                          <th className="text-right py-3 px-4 text-red-400 font-semibold">Total Depo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {websiteData.map((site) => (
                          <tr key={site.website} className="border-b border-red-900/20 hover:bg-red-900/10">
                            <td className="py-3 px-4 text-white font-medium">{site.website}</td>
                            <td className="py-3 px-4 text-center text-gray-300">{site.total_members}</td>
                            <td className="py-3 px-4 text-center text-green-400">{site.ndp_count}</td>
                            <td className="py-3 px-4 text-center text-yellow-400">{site.regis_count}</td>
                            <td className="py-3 px-4 text-right text-green-400 font-semibold">
                              Rp {site.total_depo.toLocaleString("id-ID")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
